import { cn } from '@/lib/utils'
import type { AdminRole } from '@/types'

const toneClass: Record<string, string> = {
  emerald: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  amber: 'bg-amber-100 text-amber-800 border-amber-200',
  stone: 'bg-stone-100 text-stone-700 border-stone-200',
  rose: 'bg-rose-100 text-rose-800 border-rose-200',
  teal: 'bg-teal-100 text-teal-800 border-teal-200',
  orange: 'bg-orange-100 text-orange-800 border-orange-200',
  blue: 'bg-blue-100 text-blue-800 border-blue-200',
  purple: 'bg-purple-100 text-purple-800 border-purple-200',
  indigo: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  sky: 'bg-sky-100 text-sky-800 border-sky-200',
}

export function statusTone(status: string | undefined): string {
  if (!status) return 'stone'
  
  const statusLower = status.toLowerCase()
  
  // Admin roles
  if (statusLower === 'superadmin') return 'rose'
  if (statusLower === 'admin') return 'orange'
  if (statusLower === 'editor') return 'blue'
  if (statusLower === 'viewer') return 'stone'
  
  // General statuses
  switch (statusLower) {
    case 'active':
    case 'published':
    case 'paid':
    case 'completed':
    case 'resolved':
    case 'enabled':
    case 'sync':
    case 'normal':
    case 'low':
      return 'emerald'
    
    case 'review':
    case 'pending':
    case 'draft':
    case 'in_progress':
    case 'scheduled':
    case 'invited':
    case 'elevated':
    case 'moderate':
      return 'amber'
    
    case 'disabled':
    case 'inactive':
    case 'cancelled':
    case 'archived':
    case 'closed':
    case 'out_of_stock':
      return 'stone'
    
    case 'urgent':
    case 'blocked':
    case 'high':
    case 'hypertensive crisis':
    case 'stage 2 hypertension':
    case 'critical':
      return 'rose'
    
    case 'stage 1 hypertension':
    case 'concerning':
      return 'orange'
    
    case 'open':
    case 'info':
      return 'sky'
    
    case 'general':
    case 'medium':
    default:
      return 'teal'
  }
}

interface StatusBadgeProps {
  value?: string | AdminRole
  className?: string
  /** Warna eksplisit, untuk label yang tidak ada di kamus `statusTone`. */
  tone?: keyof typeof toneClass
  /** Penjelasan saat kursor menyentuh badge. */
  title?: string
}

export function StatusBadge({ value, className, tone: toneOverride, title }: StatusBadgeProps) {
  const tone = toneOverride ?? statusTone(value)
  
  // Format role names for display
  const displayValue = value
    ? value === 'superadmin' 
      ? 'Super Admin'
      : value.charAt(0).toUpperCase() + value.slice(1)
    : 'unknown'

  return (
    <span
      title={title}
      className={cn(
        'inline-flex rounded-full px-3 py-1 text-xs font-semibold border',
        toneClass[tone],
        title && 'cursor-help',
        className
      )}
    >
      {displayValue}
    </span>
  )
}

// Specialized badges for specific use cases
export function RoleBadge({ role }: { role: AdminRole | string | undefined }) {
  const config: Record<string, { color: string; icon?: string }> = {
    superadmin: { color: 'rose', icon: '👑' },
    admin: { color: 'orange', icon: '⭐' },
    editor: { color: 'blue', icon: '📝' },
    viewer: { color: 'stone', icon: '👁️' },
  }
  
  const { color, icon } = config[role || 'viewer'] || config.viewer
  
  return (
    <span className={cn(
      'inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold border',
      toneClass[color]
    )}>
      {icon && <span>{icon}</span>}
      {role === 'superadmin' ? 'Super Admin' : role ? role.charAt(0).toUpperCase() + role.slice(1) : 'Unknown'}
    </span>
  )
}

export function HealthStatusBadge({ 
  value, 
  type 
}: { 
  value: number | string | undefined
  type: 'heartrate' | 'spo2' | 'stress' | 'bp' | 'weight'
}) {
  let status = 'normal'
  
  if (type === 'spo2') {
    const num = typeof value === 'string' ? parseFloat(value) : value || 0
    if (num < 90) status = 'critical'
    else if (num < 95) status = 'concerning'
    else status = 'normal'
  } else if (type === 'stress') {
    const num = typeof value === 'string' ? parseFloat(value) : value || 0
    if (num > 60) status = 'high'
    else if (num > 30) status = 'moderate'
    else status = 'low'
  } else if (type === 'bp') {
    status = typeof value === 'string' ? value.toLowerCase().replace(/\s+/g, '') : 'normal'
  }
  
  const tone = statusTone(status)
  
  return (
    <span className={cn(
      'inline-flex rounded-full px-2 py-0.5 text-xs font-medium border',
      toneClass[tone]
    )}>
      {typeof value === 'number' && type === 'spo2' ? `${value}%` : value}
    </span>
  )
}
